import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Tabs } from '@mantine/core';
import { IconUserPlus } from '@tabler/icons-react';
import { MutationFunctionOptions, useQuery, useMutation } from '@apollo/client';

import DefaultModuleLayout from '../../components/layouts/DefaultModuleLayout';
import { DashboardHeader } from '../../components/common';
import { UserSidesheet, UsersTable } from '../../components/users';
import { GET_USERS_PAGINATED } from '../../apollo/queries/user';
import { UPDATE_USER } from '../../apollo/mutations/user';
import {
    showErrorNotification,
    showLoadingNotification,
    updateFailedNotification,
    updateSuccessNotification,
} from '../../notifications';
import { User, HierarchyUser } from '../../codegen/graphql';
import { useUserPortal } from '../../context/UserPortalContext';

function SystemUsersPage() {
    const [activeTab, setActiveTab] = useState<string>('Active');
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [search, setSearch] = useState('');
    const { openUserSidesheet } = useUserPortal();

    const isActive = activeTab === 'Active';

    const openAddUserModal = () => {
        openUserSidesheet();
    };

    const openEditUserSidesheet = (user: User) => {
        const hierarchyUser: HierarchyUser = {
            __typename: 'HierarchyUser',
            id: user.id,
            firstName: user.firstName,
            lastName: user.lastName,
        };
        openUserSidesheet(hierarchyUser);
    };

    const {
        data: getUsersData,
        loading: fetchingUsers,
        error: getUsersError,
        refetch,
    } = useQuery(GET_USERS_PAGINATED, {
        variables: {
            page,
            pageSize,
            search: search || undefined,
            isActive,
        },
        fetchPolicy: 'cache-and-network',
    });

    const [updateUserMutation, { reset: resetUpdateUser }] = useMutation(
        UPDATE_USER,
        {
            refetchQueries: [GET_USERS_PAGINATED],
            awaitRefetchQueries: true,
        },
    );

    const updateUser = async (input: MutationFunctionOptions) => {
        showLoadingNotification(
            'update-user',
            'Updating user',
            'Updating user profile...',
        );
        try {
            await updateUserMutation(input);
            updateSuccessNotification(
                'update-user',
                'User updated successfully',
                'User profile updated successfully',
            );
            resetUpdateUser();
        } catch (error: any) {
            updateFailedNotification(
                'update-user',
                'Failed to update user',
                error.message,
            );
        }
    };

    const users = useMemo(() => {
        if (getUsersData?.getUsersPaginated) {
            return getUsersData.getUsersPaginated.users;
        }
        return [];
    }, [getUsersData]);

    const totalCount = useMemo(() => {
        return getUsersData?.getUsersPaginated?.totalCount ?? 0;
    }, [getUsersData]);

    const handleTabChange = useCallback((value: string | null) => {
        setActiveTab(value ?? 'Active');
        setPage(1);
    }, []);

    const handleSearchChange = useCallback((value: string) => {
        setSearch(value);
        setPage(1);
    }, []);

    const handlePageChange = useCallback((newPage: number) => {
        setPage(newPage);
    }, []);

    const handlePageSizeChange = useCallback((newPageSize: number) => {
        setPageSize(newPageSize);
        setPage(1);
    }, []);

    const handleRefresh = useCallback(() => {
        refetch();
    }, [refetch]);

    useEffect(() => {
        localStorage.setItem('selectedModule', 'User Management');
    }, []);

    useEffect(() => {
        if (getUsersError) {
            showErrorNotification('Error fetching users');
        }
    }, [getUsersError]);

    return (
        <DefaultModuleLayout>
            <div>
                <DashboardHeader
                    title="User Management"
                    hasBreadcrumb={false}
                    hasDate
                    isLoading={false}
                    rightComponent={
                        <Button
                            onClick={openAddUserModal}
                            leftSection={<IconUserPlus />}
                            fullWidth={false}
                            size="md"
                        >
                            Add New
                        </Button>
                    }
                />

                <Tabs
                    defaultValue="All"
                    className="px-11"
                    onChange={handleTabChange}
                    value={activeTab}
                >
                    <Tabs.List>
                        <Tabs.Tab value="Active">
                            Active Users
                            {activeTab === 'Active' && totalCount > 0
                                ? ` [${totalCount}]`
                                : ''}
                        </Tabs.Tab>
                        <Tabs.Tab value="Inactive">
                            Inactive Users
                            {activeTab === 'Inactive' && totalCount > 0
                                ? ` [${totalCount}]`
                                : ''}
                        </Tabs.Tab>
                    </Tabs.List>

                    <UsersTable
                        users={users}
                        isLoading={fetchingUsers}
                        totalCount={totalCount}
                        page={page}
                        pageSize={pageSize}
                        search={search}
                        onPageChange={handlePageChange}
                        onPageSizeChange={handlePageSizeChange}
                        onSearchChange={handleSearchChange}
                        onRefresh={handleRefresh}
                        openEditUserSidesheet={openEditUserSidesheet}
                        updateUser={updateUser}
                    />
                </Tabs>
            </div>
            <UserSidesheet />
        </DefaultModuleLayout>
    );
}

export default SystemUsersPage;
